/* eslint-disable no-underscore-dangle */
import type { VoidFunctionComponent } from 'react';
import shortid from 'shortid';
import type { GitAuth, GitTokenAuth, GitUserPasswordAuth } from '../../../../../typings/auth';
import { window } from '../../../../globals';
import { dropAt } from '../../../../utils';
import { icons } from '../../../generic/Icon';
import { AccessGate } from '../../../generic/Gates';
import Button, { ButtonGroup, IconButton } from '../../../generic/Button';
import Flex from '../../../generic/Flex';
import Loader from '../../../generic/Loader';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import useLanguageSettings from '../useLanguageSettings';
import GitHeader from './GitHeader';
import { GitDisplayNameInput, GitTokenAuthInput, GitUserPasswordAuthInputs } from './inputs';

const newTokenAuth = (): GitTokenAuth =>
  ({ _id: shortid.generate(), type: 'oauth_key', value: { __type: 's', value: '' } });

const newUserPasswordAuth = (): GitUserPasswordAuth =>
  ({ _id: shortid.generate(), type: 'basic', username: '', password: { __type: 's', value: '' } });

const GitSettings: VoidFunctionComponent = () => {
  const {
    settings,
    isSaving,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled,
    isLoading,
  } = useLanguageSettings(
    window.user?.organizationId as number,
    'git',
  );

  const authOptions = settings?.authOptions || [];
  // NOTE(privateToken): previously, we only allowed configuring a single git
  // auth token. If an organization has already configured one, we preserve it
  // by keeping the hasPrivateToken field
  const hasPrivateToken = !!settings?.hasPrivateToken;

  // Add a new auth option
  const onAddAuthOption = (option: GitAuth) => {
    onUpdateSettings({
      authOptions: [
        ...authOptions,
        option,
      ],
    });
  };

  // Replace an auth option
  const onUpdateAuthOption = (index: number, option: GitAuth) =>
    onUpdateSettings({
      authOptions: authOptions.map((auth, i) => {
        if (i !== index) {
          return auth;
        }

        return option;
      }),
    });

  // Remove an auth option by index
  const onRemoveAuthOption = (index: number) => {
    onUpdateSettings({
      authOptions: dropAt(authOptions, index),
    });
  };

  return (
    <AccessGate level="premium">
      <Header heading="Git Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isDisabled={isSaveButtonDisabled}
          isLoading={isSaving}
        />
      </Header>
      {isLoading
        ? <Loader />
        : (
          <Section>
            <Section.Left heading="Repo Authentication">
              <Section.Paragraph>
                Git authentication settings. You can provide FOSSA with an Access token so that we can resolve any
                {' '}
                private Golang packages, git submodules, or any other git projects.
              </Section.Paragraph>
            </Section.Left>
            <Section.Right>
              {hasPrivateToken && (
                // Display a dummy token so that the user knows they have a private token configured and can delete it.
                <div className="card">
                  <Flex justify="space-between" gap="14px">
                    <GitTokenAuthInput
                      auth={{
                        _id: '',
                        type: 'oauth_key',
                        hasToken: true,
                      }}
                      update={() => {}}
                    />
                    <IconButton
                      id="remove-token-btn"
                      onClick={() => {
                        onUpdateSettings({
                          // TODO: is there a better way to "clear" the property out?
                          hasPrivateToken: undefined,
                        });
                      }}
                      icon={icons.trash}
                      tooltip="Remove"
                    />
                  </Flex>
                </div>
              )}
              {authOptions.map((auth, index) => (
                <div key={auth._id} className="card">
                  <GitHeader auth={auth} onDelete={() => onRemoveAuthOption(index)} />
                  <GitDisplayNameInput
                    auth={auth}
                    update={(newAuth) => onUpdateAuthOption(index, newAuth)}
                  />
                  <Flex justify="space-between" gap="14px">
                    {auth.type === 'oauth_key' && (
                      <GitTokenAuthInput
                        auth={auth}
                        update={(newAuth) => onUpdateAuthOption(index, newAuth)}
                      />
                    )}
                    {auth.type === 'basic' && (
                      <GitUserPasswordAuthInputs
                        auth={auth}
                        update={(newAuth) => onUpdateAuthOption(index, newAuth)}
                      />
                    )}
                  </Flex>
                </div>
              ))}
              <ButtonGroup>
                <Button
                  onClick={() => onAddAuthOption(newTokenAuth())}
                  type="secondary"
                  size="medium"
                  icon={icons.plus}
                >
                  Access Token
                </Button>
                <Button
                  onClick={() => onAddAuthOption(newUserPasswordAuth())}
                  type="secondary"
                  size="medium"
                  icon={icons.plus}
                >
                  Username/Password
                </Button>
              </ButtonGroup>
            </Section.Right>
          </Section>
          )}
    </AccessGate>
  );
};

export default GitSettings;
